/**
 * The tour sheet (WS-2, fieldiq-api#1282), shared by the office page and the
 * phone. Each surface maps its read into TourValues (tourValues.ts); the form,
 * its rules and the body it sends are one.
 *
 * ONE SHEET PER RIG PER LOCAL WORK DAY. Times are 'HH:MM' in the tenant's
 * zone; a time before the sheet's start belongs to the next calendar day (a
 * sheet may run past midnight) — the API places it, not this screen.
 *
 * EVERY HOUR IS THE API'S. A row's hours, the sheet's length, the covered
 * hours, the gaps and the totals are computed by the API from the stored times
 * and shown as sent. A row changed since the last save shows `—` for its hours
 * until it is saved; nothing here adds up a time.
 *
 * The body always sends all four sets (activities, crew, equipment, fuel) and
 * the API REPLACES each, so a row removed here is a row removed there. Gaps
 * are allowed while the sheet is a draft; submitting refuses until the
 * activities cover the sheet end to end and there is crew.
 */
import React, { useMemo, useRef, useState } from 'react';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { blankToNull, catalogLabel, fmtWsNumber, toInput, wsBillingSkin, wsHours, wsSpan, WS_TIME_PATTERN } from '../../lib/well-servicing';
import type { WsActivityCode, WsTourWrite } from '../../types/well-servicing';
import {
  unitKey,
  type TourCatalog,
  type TourCrewOption,
  type TourUnitOption,
  type TourValues,
} from './tourValues';
import { WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsBadge, WsEmpty, WsField, WsFormError, WsPanel, WsSpec } from './WsBits';

// ─── Rows (the form's own state) ─────────────────────────────────────────────

interface Row {
  rk: number;
  /** The API's hours for this row as saved; null once the row is edited. */
  savedHours: number | null;
}
export interface ActivityRow extends Row { from: string; to: string; activityCodeId: string; code: string; description: string; depth: string; depthUnit: string }
export interface CrewRow extends Row { userId: string; position: string; crewShift: string; on: string; off: string; travelHours: string; subsistence: boolean; notes: string }
export interface UnitRow extends Row { unit: string; hours: string; meter: string; notes: string }
export interface FuelRow extends Row { entryKind: string; fuelType: string; quantity: string; unit: string; equipmentId: string; supplierRef: string; notes: string }
export interface TourForm {
  startTime: string;
  endTime: string;
  depthSummary: string;
  remarks: string;
  activities: ActivityRow[];
  crew: CrewRow[];
  equipment: UnitRow[];
  fuel: FuelRow[];
}

let nextRk = 1;
const rk = () => nextRk++;

export function tourFormFrom(v: TourValues, defaults: { depthUnit: string; fuelUnit: string }): TourForm {
  return {
    startTime: v.startTime,
    endTime: v.endTime,
    depthSummary: v.depthSummary ?? '',
    remarks: v.remarks ?? '',
    activities: v.activities.map((a) => ({
      rk: rk(), savedHours: a.hours, from: a.from, to: a.to, activityCodeId: a.activityCodeId ?? '', code: a.code,
      description: a.description ?? '', depth: a.depth ? toInput(a.depth.value) : '', depthUnit: a.depth?.unit ?? defaults.depthUnit,
    })),
    crew: v.crew.map((c) => ({
      rk: rk(), savedHours: c.hours, userId: c.userId, position: c.position, crewShift: c.crewShift || 'day', on: c.on ?? '', off: c.off ?? '',
      travelHours: c.travelHours ? toInput(c.travelHours) : '', subsistence: c.subsistence, notes: c.notes ?? '',
    })),
    equipment: v.equipment.map((u) => ({
      rk: rk(), savedHours: u.hours, unit: unitKey(u.kind, u.equipmentId, u.rentInId), hours: toInput(u.hours),
      meter: toInput(u.hourMeterEnd), notes: u.notes ?? '',
    })),
    fuel: v.fuel.map((f) => ({
      rk: rk(), savedHours: null, entryKind: f.entryKind, fuelType: f.fuelType, quantity: f.quantity ? toInput(f.quantity.value) : '',
      unit: f.quantity?.unit ?? defaults.fuelUnit, equipmentId: f.equipmentId ?? '', supplierRef: f.supplierRef ?? '', notes: f.notes ?? '',
    })),
  };
}

const NUMBER = /^\d+(\.\d+)?$/;

/**
 * The write body, or the first reason it cannot be sent. Every set is sent;
 * a blank crew on / off is sent as null (the API's default is the sheet
 * window). Exported for the test.
 */
export function tourWriteBody(form: TourForm, units: TourUnitOption[]): { body: WsTourWrite } | { error: string } {
  const t = (s: string) => s.trim();
  if (t(form.startTime) && !WS_TIME_PATTERN.test(t(form.startTime))) return { error: 'The sheet start must be a time like 06:00.' };
  if (t(form.endTime) && !WS_TIME_PATTERN.test(t(form.endTime))) return { error: 'The sheet end must be a time like 18:00.' };
  const activities: NonNullable<WsTourWrite['activities']> = [];
  for (let i = 0; i < form.activities.length; i++) {
    const a = form.activities[i];
    const n = i + 1;
    if (!WS_TIME_PATTERN.test(t(a.from)) || !WS_TIME_PATTERN.test(t(a.to))) return { error: `Activity ${n}: enter the from and to times like 07:30.` };
    if (!a.activityCodeId && !t(a.code)) return { error: `Activity ${n}: choose the activity code.` };
    if (t(a.depth) && !NUMBER.test(t(a.depth))) return { error: `Activity ${n}: the depth must be a number, or blank.` };
    activities.push({
      from: t(a.from),
      to: t(a.to),
      ...(a.activityCodeId ? { activity_code_id: a.activityCodeId } : { code: t(a.code) }),
      description: blankToNull(a.description),
      ...(t(a.depth) ? { depth: t(a.depth), depth_unit: a.depthUnit } : {}),
    });
  }
  const crew: NonNullable<WsTourWrite['crew']> = [];
  for (let i = 0; i < form.crew.length; i++) {
    const c = form.crew[i];
    const n = i + 1;
    if (!c.userId) return { error: `Crew ${n}: choose the person.` };
    if (!c.position) return { error: `Crew ${n}: choose the position.` };
    if ((t(c.on) && !WS_TIME_PATTERN.test(t(c.on))) || (t(c.off) && !WS_TIME_PATTERN.test(t(c.off))))
      return { error: `Crew ${n}: on and off are times like 06:00, or blank for the whole sheet.` };
    if (t(c.travelHours) && !NUMBER.test(t(c.travelHours))) return { error: `Crew ${n}: travel hours must be a number, or blank.` };
    crew.push({
      user_id: c.userId,
      position: c.position,
      crew_shift: c.crewShift || 'day',
      on: blankToNull(c.on),
      off: blankToNull(c.off),
      travel_hours: blankToNull(c.travelHours),
      subsistence: c.subsistence,
      notes: blankToNull(c.notes),
    });
  }
  const equipment: NonNullable<WsTourWrite['equipment']> = [];
  for (let i = 0; i < form.equipment.length; i++) {
    const u = form.equipment[i];
    const n = i + 1;
    const opt = units.find((o) => o.key === u.unit);
    if (!opt) return { error: `Equipment ${n}: choose the unit.` };
    if (!NUMBER.test(t(u.hours))) return { error: `Equipment ${n}: enter the hours it ran.` };
    if (t(u.meter) && opt.kind !== 'owned') return { error: `Equipment ${n}: an hour meter is recorded for an owned unit only.` };
    if (t(u.meter) && !NUMBER.test(t(u.meter))) return { error: `Equipment ${n}: the hour meter must be a number, or blank.` };
    equipment.push({
      ...(opt.kind === 'rent_in' ? { rent_in_id: opt.rentInId as string } : { equipment_id: opt.equipmentId as string }),
      hours: t(u.hours),
      ...(opt.kind === 'owned' ? { hour_meter_end: blankToNull(u.meter) } : {}),
      notes: blankToNull(u.notes),
    });
  }
  const fuel: NonNullable<WsTourWrite['fuel']> = [];
  for (let i = 0; i < form.fuel.length; i++) {
    const f = form.fuel[i];
    const n = i + 1;
    if (!f.entryKind) return { error: `Fuel ${n}: choose on hand, received or used.` };
    if (!NUMBER.test(t(f.quantity))) return { error: `Fuel ${n}: enter the quantity.` };
    fuel.push({
      entry_kind: f.entryKind,
      fuel_type: f.fuelType || 'diesel',
      quantity: t(f.quantity),
      unit: f.unit,
      supplier_ref: blankToNull(f.supplierRef),
      equipment_id: f.equipmentId || null,
      notes: blankToNull(f.notes),
    });
  }
  return {
    body: {
      ...(t(form.startTime) ? { start_time: t(form.startTime) } : {}),
      ...(t(form.endTime) ? { end_time: t(form.endTime) } : {}),
      depth_summary: blankToNull(form.depthSummary),
      remarks: blankToNull(form.remarks),
      activities,
      crew,
      equipment,
      fuel,
    },
  };
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

function AddRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={WS_SECONDARY} onClick={onClick}>
      <Plus className="size-4" aria-hidden="true" />
      {label}
    </button>
  );
}

function RemoveRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={`${WS_SECONDARY} self-end`} aria-label={label} onClick={onClick}>
      <Trash2 className="size-4" aria-hidden="true" />
    </button>
  );
}

function RowHours({ hours }: { hours: number | null }) {
  return (
    <div className="min-w-0 self-end pb-2 text-right">
      <span className="block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Hours</span>
      <span className="font-mono text-sm tabular-nums text-white">{wsHours(hours)}</span>
    </div>
  );
}

const ROW = 'grid gap-3 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3';

/** An hour-meter end below the unit's previous reading: saved anyway, shown after the save. */
export function TourMeterWarnings({
  warnings,
  unitName,
}: {
  warnings: { equipmentId: string | null; entered: number; previous: number; previousDate: string | null }[];
  unitName: (equipmentId: string | null) => string | null;
}) {
  if (!warnings.length) return null;
  return (
    <div role="status" data-testid="ws-tour-warnings" className="flex gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-amber" aria-hidden="true" />
      <ul className="space-y-1">
        {warnings.map((w, i) => (
          <li key={i}>
            {unitName(w.equipmentId) ?? 'A unit'}: the hour meter {fmtWsNumber(w.entered)} is below the last reading of {fmtWsNumber(w.previous)}
            {w.previousDate ? ` (${w.previousDate})` : ''}. It was saved; check it.
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The API's figures for the saved sheet: length, coverage, gaps, and (office) the splits. */
export function TourSummary({ values, billingClasses }: { values: TourValues; billingClasses?: { value: string; label: string }[] }) {
  const tl = values.timeline;
  return (
    <WsPanel eyebrow="Tour sheet" title="Time on the sheet" testId="ws-tour-summary">
      <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <WsSpec label="Sheet" value={`${wsSpan(values.startTime, values.endTime)} · ${wsHours(tl.tourHours)}`} mono />
        <WsSpec label="Activities cover" value={wsHours(tl.coveredHours)} mono />
        <WsSpec label="Covered" value={tl.complete ? 'End to end' : `${tl.gaps.length} ${tl.gaps.length === 1 ? 'gap' : 'gaps'}`} />
        {values.totals && <WsSpec label="Crew hours" value={wsHours(values.totals.crewHours)} mono />}
      </dl>
      {!tl.complete && tl.gaps.length > 0 && (
        <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text" data-testid="ws-tour-gaps">
          <span className="font-bold">Gaps: </span>
          {tl.gaps.map((g) => wsSpan(g.from, g.to)).join(', ')}
        </p>
      )}
      {values.hoursByBillingClass && (
        <dl className="grid gap-4 border-t border-fieldiq-border px-4 py-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="ws-tour-billing">
          {Object.entries(values.hoursByBillingClass).map(([k, h]) => (
            <WsSpec key={k} label={catalogLabel(billingClasses, k)} value={wsHours(h)} mono />
          ))}
        </dl>
      )}
      {values.totals && (
        <dl className="grid gap-4 border-t border-fieldiq-border px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <WsSpec label="Crew travel" value={wsHours(values.totals.crewTravelHours)} mono />
          <WsSpec label="Owned equipment" value={wsHours(values.totals.ownedEquipmentHours)} mono />
          <WsSpec label="Rent-ins" value={wsHours(values.totals.rentInHours)} mono />
          <WsSpec
            label="Fuel used"
            value={values.totals.fuelLitres && values.totals.fuelLitres.used != null ? `${fmtWsNumber(values.totals.fuelLitres.used)} L` : '—'}
            mono
          />
        </dl>
      )}
    </WsPanel>
  );
}

// ─── The read-only sheet (submitted, approved, or a role that cannot edit) ──

export function TourReadout({ values, catalog, unitOptions }: { values: TourValues; catalog: TourCatalog; unitOptions: TourUnitOption[] }) {
  const unitName = (id: string | null) => unitOptions.find((u) => u.equipmentId === id)?.name ?? null;
  return (
    <div className="space-y-4" data-testid="ws-tour-readout">
      <WsPanel eyebrow="Activities" title="What the rig did">
        {values.activities.length === 0 ? (
          <WsEmpty>No activity is on this sheet.</WsEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border">
            {values.activities.map((a, i) => (
              <li key={i} className="flex flex-wrap items-start justify-between gap-2 px-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className="font-mono font-bold text-white">{wsSpan(a.from, a.to)} · {a.code}</p>
                  <p className="mt-0.5 text-fieldiq-text">{a.codeLabel || '—'}{a.description ? ` — ${a.description}` : ''}</p>
                  {a.depth && <p className="mt-0.5 text-xs text-fieldiq-muted">Depth {fmtWsNumber(a.depth.value)} {a.depth.unit}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <WsBadge skin={wsBillingSkin(a.billingClass)} value={a.billingClass} />
                  <span className="font-mono tabular-nums text-white">{wsHours(a.hours)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </WsPanel>
      <WsPanel eyebrow="Crew" title="Who worked">
        {values.crew.length === 0 ? (
          <WsEmpty>No crew is on this sheet.</WsEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border">
            {values.crew.map((c, i) => (
              <li key={i} className="flex flex-wrap items-start justify-between gap-2 px-4 py-3 text-sm">
                <div>
                  <p className="font-bold text-white">{c.userName || '—'}</p>
                  <p className="mt-0.5 text-fieldiq-text">
                    {catalogLabel(catalog.crewPositions, c.position)} · {catalogLabel(catalog.crewShifts, c.crewShift)} · {wsSpan(c.on, c.off)}
                  </p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                    Travel {wsHours(c.travelHours)} · Subsistence {c.subsistence ? 'yes' : 'no'}{c.notes ? ` · ${c.notes}` : ''}
                  </p>
                </div>
                <span className="font-mono tabular-nums text-white">{wsHours(c.hours)}</span>
              </li>
            ))}
          </ul>
        )}
      </WsPanel>
      <WsPanel eyebrow="Equipment" title="Unit hours">
        {values.equipment.length === 0 ? (
          <WsEmpty>No unit hours are on this sheet.</WsEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border">
            {values.equipment.map((u, i) => (
              <li key={i} className="flex flex-wrap items-start justify-between gap-2 px-4 py-3 text-sm">
                <div>
                  <p className="font-bold text-white">{u.name || '—'}</p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                    {u.kind === 'rent_in' ? 'Rent-in' : 'Owned'} · Hour meter {fmtWsNumber(u.hourMeterEnd)}{u.notes ? ` · ${u.notes}` : ''}
                  </p>
                </div>
                <span className="font-mono tabular-nums text-white">{wsHours(u.hours)}</span>
              </li>
            ))}
          </ul>
        )}
      </WsPanel>
      <WsPanel eyebrow="Fuel" title="Fuel">
        {values.fuel.length === 0 ? (
          <WsEmpty>No fuel is on this sheet.</WsEmpty>
        ) : (
          <ul className="divide-y divide-fieldiq-border">
            {values.fuel.map((f, i) => (
              <li key={i} className="flex flex-wrap items-start justify-between gap-2 px-4 py-3 text-sm">
                <div>
                  <p className="font-bold text-white">
                    {catalogLabel(catalog.fuelEntryKinds, f.entryKind)} · {catalogLabel(catalog.fuelTypes, f.fuelType)}
                  </p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                    {f.equipmentId ? unitName(f.equipmentId) ?? 'A unit' : 'No unit'}{f.supplierRef ? ` · Ref ${f.supplierRef}` : ''}{f.notes ? ` · ${f.notes}` : ''}
                  </p>
                </div>
                <span className="font-mono tabular-nums text-white">{f.quantity ? `${fmtWsNumber(f.quantity.value)} ${f.quantity.unit}` : '—'}</span>
              </li>
            ))}
          </ul>
        )}
      </WsPanel>
      {(values.depthSummary || values.remarks) && (
        <WsPanel eyebrow="Notes" title="Depth and remarks">
          <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2">
            <WsSpec label="Depth summary" value={values.depthSummary || '—'} />
            <WsSpec label="Remarks" value={values.remarks || '—'} />
          </dl>
        </WsPanel>
      )}
    </div>
  );
}

// ─── The editor ──────────────────────────────────────────────────────────────

export function TourEditor({
  values,
  codes,
  crewOptions,
  unitOptions,
  catalog,
  defaults,
  pending,
  error,
  submitLabel,
  onSubmit,
}: {
  values: TourValues;
  codes: WsActivityCode[];
  crewOptions: TourCrewOption[];
  unitOptions: TourUnitOption[];
  catalog: TourCatalog;
  defaults: { depthUnit: string; fuelUnit: string };
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onSubmit: (body: WsTourWrite) => void;
}) {
  const [form, setForm] = useState<TourForm>(() => tourFormFrom(values, defaults));
  const [localError, setLocalError] = useState<string | null>(null);
  const top = useRef<HTMLDivElement>(null);
  // Active codes plus any retired code the sheet already carries (the API keeps those).
  const codeOptions = useMemo(() => {
    const held = new Set(values.activities.map((a) => a.activityCodeId).filter(Boolean));
    return codes.filter((c) => c.active || held.has(c.id));
  }, [codes, values.activities]);
  const ownedOptions = unitOptions.filter((u) => u.kind === 'owned');

  type SetKey = 'activities' | 'crew' | 'equipment' | 'fuel';
  const patch = <K extends SetKey>(key: K, i: number, change: Partial<TourForm[K][number]>) =>
    setForm((f) => ({
      ...f,
      [key]: (f[key] as Row[]).map((r, j) => (j === i ? { ...r, ...change, savedHours: null } : r)),
    }));
  const drop = (key: SetKey, i: number) => setForm((f) => ({ ...f, [key]: (f[key] as Row[]).filter((_, j) => j !== i) }));
  const lastTo = form.activities.length ? form.activities[form.activities.length - 1].to : form.startTime;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const out = tourWriteBody(form, unitOptions);
    if ('error' in out) {
      setLocalError(out.error);
      top.current?.scrollIntoView?.({ block: 'start' });
      return;
    }
    onSubmit(out.body);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="ws-tour-editor">
      <div ref={top}>
        <WsFormError message={localError ?? error} />
      </div>

      <WsPanel eyebrow="Tour sheet" title="Sheet window">
        <div className="grid gap-3 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <WsField label="Start" hint="HH:MM, the tenant's time">
            <input className={WS_INPUT} inputMode="numeric" placeholder="06:00" aria-label="Sheet start" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
          </WsField>
          <WsField label="End" hint="Before the start means the next morning">
            <input className={WS_INPUT} inputMode="numeric" placeholder="18:00" aria-label="Sheet end" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
          </WsField>
          <WsField label="Depth summary">
            <input className={WS_INPUT} aria-label="Depth summary" value={form.depthSummary} onChange={(e) => setForm({ ...form, depthSummary: e.target.value })} />
          </WsField>
          <WsField label="Remarks">
            <input className={WS_INPUT} aria-label="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
          </WsField>
        </div>
      </WsPanel>

      <WsPanel
        eyebrow="Activities"
        title="What the rig did"
        testId="ws-tour-activities"
        actions={
          <AddRow
            label="Add activity"
            onClick={() =>
              setForm((f) => ({
                ...f,
                activities: [...f.activities, { rk: rk(), savedHours: null, from: lastTo, to: '', activityCodeId: '', code: '', description: '', depth: '', depthUnit: defaults.depthUnit }],
              }))
            }
          />
        }
      >
        <div className="space-y-3 px-4 py-4">
          {form.activities.length === 0 && <WsEmpty>No activity yet. Add one for each stretch of the tour.</WsEmpty>}
          {form.activities.map((a, i) => (
            <div key={a.rk} className={`${ROW} sm:grid-cols-[6rem_6rem_minmax(0,1fr)_minmax(0,1fr)_7rem_5rem_auto]`} data-testid="ws-activity-row">
              <WsField label="From">
                <input className={WS_INPUT} inputMode="numeric" placeholder="07:00" aria-label={`Activity ${i + 1} from`} value={a.from} onChange={(e) => patch('activities', i, { from: e.target.value })} />
              </WsField>
              <WsField label="To">
                <input className={WS_INPUT} inputMode="numeric" placeholder="09:30" aria-label={`Activity ${i + 1} to`} value={a.to} onChange={(e) => patch('activities', i, { to: e.target.value })} />
              </WsField>
              <WsField label="Code">
                <select className={WS_INPUT} aria-label={`Activity ${i + 1} code`} value={a.activityCodeId} onChange={(e) => patch('activities', i, { activityCodeId: e.target.value, code: '' })}>
                  <option value="">{a.code && !a.activityCodeId ? a.code : 'Choose…'}</option>
                  {codeOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} · {c.label}
                      {c.active ? '' : ' (retired)'}
                    </option>
                  ))}
                </select>
              </WsField>
              <WsField label="Description">
                <input className={WS_INPUT} aria-label={`Activity ${i + 1} description`} value={a.description} onChange={(e) => patch('activities', i, { description: e.target.value })} />
              </WsField>
              <WsField label="Depth">
                <div className="flex gap-1">
                  <input className={WS_INPUT} inputMode="decimal" aria-label={`Activity ${i + 1} depth`} value={a.depth} onChange={(e) => patch('activities', i, { depth: e.target.value })} />
                  <select className={`${WS_INPUT} w-16 shrink-0`} aria-label={`Activity ${i + 1} depth unit`} value={a.depthUnit} onChange={(e) => patch('activities', i, { depthUnit: e.target.value })}>
                    {catalog.depthUnits.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              </WsField>
              <RowHours hours={a.savedHours} />
              <RemoveRow label={`Remove activity ${i + 1}`} onClick={() => drop('activities', i)} />
            </div>
          ))}
        </div>
      </WsPanel>

      <WsPanel
        eyebrow="Crew"
        title="Who worked"
        testId="ws-tour-crew"
        actions={
          <AddRow
            label="Add hand"
            onClick={() =>
              setForm((f) => ({
                ...f,
                crew: [...f.crew, { rk: rk(), savedHours: null, userId: '', position: '', crewShift: 'day', on: '', off: '', travelHours: '', subsistence: false, notes: '' }],
              }))
            }
          />
        }
      >
        <div className="space-y-3 px-4 py-4">
          {form.crew.length === 0 && <WsEmpty>No crew yet. A sheet cannot be submitted without crew.</WsEmpty>}
          {form.crew.map((c, i) => (
            <div key={c.rk} className={`${ROW} sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_7rem_5.5rem_5.5rem_5.5rem_auto_5rem_auto]`} data-testid="ws-crew-row">
              <WsField label="Person">
                <select
                  className={WS_INPUT}
                  aria-label={`Crew ${i + 1} person`}
                  value={c.userId}
                  onChange={(e) => {
                    const pick = crewOptions.find((o) => o.userId === e.target.value);
                    patch('crew', i, { userId: e.target.value, ...(pick?.position && !c.position ? { position: pick.position } : {}) });
                  }}
                >
                  <option value="">Choose…</option>
                  {crewOptions.map((o) => (
                    <option key={o.userId} value={o.userId}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </WsField>
              <WsField label="Position">
                <select className={WS_INPUT} aria-label={`Crew ${i + 1} position`} value={c.position} onChange={(e) => patch('crew', i, { position: e.target.value })}>
                  <option value="">Choose…</option>
                  {catalog.crewPositions.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </WsField>
              <WsField label="Crew">
                <select className={WS_INPUT} aria-label={`Crew ${i + 1} shift`} value={c.crewShift} onChange={(e) => patch('crew', i, { crewShift: e.target.value })}>
                  {(catalog.crewShifts.length ? catalog.crewShifts : [{ value: 'day', label: 'Day crew' }, { value: 'night', label: 'Night crew' }]).map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </WsField>
              <WsField label="On">
                <input className={WS_INPUT} inputMode="numeric" placeholder={form.startTime || 'start'} aria-label={`Crew ${i + 1} on`} value={c.on} onChange={(e) => patch('crew', i, { on: e.target.value })} />
              </WsField>
              <WsField label="Off">
                <input className={WS_INPUT} inputMode="numeric" placeholder={form.endTime || 'end'} aria-label={`Crew ${i + 1} off`} value={c.off} onChange={(e) => patch('crew', i, { off: e.target.value })} />
              </WsField>
              <WsField label="Travel h">
                <input className={WS_INPUT} inputMode="decimal" aria-label={`Crew ${i + 1} travel hours`} value={c.travelHours} onChange={(e) => patch('crew', i, { travelHours: e.target.value })} />
              </WsField>
              <label className="flex items-center gap-2 self-end pb-2 text-sm text-fieldiq-text">
                <input type="checkbox" aria-label={`Crew ${i + 1} subsistence`} checked={c.subsistence} onChange={(e) => patch('crew', i, { subsistence: e.target.checked })} />
                Subsistence
              </label>
              <RowHours hours={c.savedHours} />
              <RemoveRow label={`Remove crew ${i + 1}`} onClick={() => drop('crew', i)} />
            </div>
          ))}
        </div>
      </WsPanel>

      <WsPanel
        eyebrow="Equipment"
        title="Unit hours"
        testId="ws-tour-equipment"
        actions={
          <AddRow
            label="Add unit"
            onClick={() => setForm((f) => ({ ...f, equipment: [...f.equipment, { rk: rk(), savedHours: null, unit: '', hours: '', meter: '', notes: '' }] }))}
          />
        }
      >
        <div className="space-y-3 px-4 py-4">
          {form.equipment.length === 0 && <WsEmpty>No unit hours yet. The rig, its BOP stack, the job&apos;s units and rent-ins on rent today can be added.</WsEmpty>}
          {form.equipment.map((u, i) => {
            const opt = unitOptions.find((o) => o.key === u.unit);
            return (
              <div key={u.rk} className={`${ROW} sm:grid-cols-[minmax(0,1.6fr)_6rem_7rem_minmax(0,1fr)_auto]`} data-testid="ws-unit-row">
                <WsField label="Unit">
                  <select className={WS_INPUT} aria-label={`Equipment ${i + 1} unit`} value={u.unit} onChange={(e) => patch('equipment', i, { unit: e.target.value })}>
                    <option value="">Choose…</option>
                    {unitOptions.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.name}
                        {o.detail ? ` · ${o.detail}` : ''}
                      </option>
                    ))}
                  </select>
                </WsField>
                <WsField label="Hours">
                  <input className={WS_INPUT} inputMode="decimal" aria-label={`Equipment ${i + 1} hours`} value={u.hours} onChange={(e) => patch('equipment', i, { hours: e.target.value })} />
                </WsField>
                {opt?.kind === 'rent_in' ? (
                  <div className="self-end pb-2 text-xs text-fieldiq-muted">No meter on a rent-in</div>
                ) : (
                  <WsField label="Hour meter">
                    <input className={WS_INPUT} inputMode="decimal" aria-label={`Equipment ${i + 1} hour meter`} value={u.meter} onChange={(e) => patch('equipment', i, { meter: e.target.value })} />
                  </WsField>
                )}
                <WsField label="Notes">
                  <input className={WS_INPUT} aria-label={`Equipment ${i + 1} notes`} value={u.notes} onChange={(e) => patch('equipment', i, { notes: e.target.value })} />
                </WsField>
                <RemoveRow label={`Remove equipment ${i + 1}`} onClick={() => drop('equipment', i)} />
              </div>
            );
          })}
        </div>
      </WsPanel>

      <WsPanel
        eyebrow="Fuel"
        title="Fuel"
        testId="ws-tour-fuel"
        actions={
          <AddRow
            label="Add fuel"
            onClick={() =>
              setForm((f) => ({
                ...f,
                fuel: [...f.fuel, { rk: rk(), savedHours: null, entryKind: 'used', fuelType: 'diesel', quantity: '', unit: defaults.fuelUnit, equipmentId: '', supplierRef: '', notes: '' }],
              }))
            }
          />
        }
      >
        <div className="space-y-3 px-4 py-4">
          {form.fuel.length === 0 && <WsEmpty>No fuel entry yet.</WsEmpty>}
          {form.fuel.map((f, i) => (
            <div key={f.rk} className={`${ROW} sm:grid-cols-[7rem_7rem_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]`} data-testid="ws-fuel-row">
              <WsField label="Entry">
                <select className={WS_INPUT} aria-label={`Fuel ${i + 1} entry`} value={f.entryKind} onChange={(e) => patch('fuel', i, { entryKind: e.target.value })}>
                  {catalog.fuelEntryKinds.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </WsField>
              <WsField label="Fuel">
                <select className={WS_INPUT} aria-label={`Fuel ${i + 1} type`} value={f.fuelType} onChange={(e) => patch('fuel', i, { fuelType: e.target.value })}>
                  {catalog.fuelTypes.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </WsField>
              <WsField label="Quantity">
                <div className="flex gap-1">
                  <input className={WS_INPUT} inputMode="decimal" aria-label={`Fuel ${i + 1} quantity`} value={f.quantity} onChange={(e) => patch('fuel', i, { quantity: e.target.value })} />
                  <select className={`${WS_INPUT} w-20 shrink-0`} aria-label={`Fuel ${i + 1} unit`} value={f.unit} onChange={(e) => patch('fuel', i, { unit: e.target.value })}>
                    {catalog.fuelUnits.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              </WsField>
              <WsField label="Into unit">
                <select className={WS_INPUT} aria-label={`Fuel ${i + 1} unit fuelled`} value={f.equipmentId} onChange={(e) => patch('fuel', i, { equipmentId: e.target.value })}>
                  <option value="">None</option>
                  {ownedOptions.map((o) => (
                    <option key={o.key} value={o.equipmentId ?? ''}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </WsField>
              <WsField label="Ticket / ref">
                <input className={WS_INPUT} aria-label={`Fuel ${i + 1} reference`} value={f.supplierRef} onChange={(e) => patch('fuel', i, { supplierRef: e.target.value })} />
              </WsField>
              <RemoveRow label={`Remove fuel ${i + 1}`} onClick={() => drop('fuel', i)} />
            </div>
          ))}
        </div>
      </WsPanel>

      <div className="flex flex-wrap justify-end gap-2">
        <button type="submit" className={WS_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

/** The catalog lists from GET /meta, with the fallbacks a pre-WS-2 payload needs. */
export function tourCatalog(c: {
  crewPositions?: { value: string; label: string }[];
  crewShifts?: { value: string; label: string }[];
  fuelEntryKinds?: { value: string; label: string }[];
  fuelTypes?: { value: string; label: string }[];
  fuelUnits?: string[];
  depthUnits?: string[];
} | null): TourCatalog {
  return {
    crewPositions: c?.crewPositions ?? [],
    crewShifts: c?.crewShifts ?? [],
    fuelEntryKinds: c?.fuelEntryKinds?.length ? c.fuelEntryKinds : [{ value: 'on_hand', label: 'On hand' }, { value: 'received', label: 'Received' }, { value: 'used', label: 'Used' }],
    fuelTypes: c?.fuelTypes?.length ? c.fuelTypes : [{ value: 'diesel', label: 'Diesel' }],
    fuelUnits: c?.fuelUnits?.length ? c.fuelUnits : ['L', 'gal'],
    depthUnits: c?.depthUnits?.length ? c.depthUnits : ['m', 'ft'],
  };
}

/** The tenant's units: imperial enters gallons and feet, metric litres and metres. */
export const tourDefaults = (unitSystem: string | null | undefined, depthUnit?: string | null) => ({
  depthUnit: depthUnit === 'ft' || depthUnit === 'm' ? depthUnit : unitSystem === 'imperial' ? 'ft' : 'm',
  fuelUnit: unitSystem === 'imperial' ? 'gal' : 'L',
});
